import { profile2OrientCopy } from "building-common/profile2Utils";
import type { OpeningSide, OpeningSideLayer } from "building-types/openings/openingTypes";
import type { PolyLine2sWithPlane } from "building-types/walls/PolyLine2sWithPlane";
import { planeProjectPolyLine3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2AABB2, polyLine2SetCCW, polyLine2SetCW } from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3OnPlane } from "geom/polyLine3";
import { mirrorPolyLine3, mirrorPolyLine2XCopy } from "../../utils/mirror";

export function reprojectProfiles(side: OpeningSide, srcPlane: Plane, destPlane: Plane): OpeningSide {
  const [opening3, opening2] = profileReproject(side.opening2, srcPlane, destPlane);
  const [openingCasing3, openingCasing2] = profileReproject(side.openingCasing2, srcPlane, destPlane);
  const [, openingRect2] = profileReproject(side.openingRect2, srcPlane, destPlane);

  // discard 3D projections for casing profiles
  let apronCasing: PolyLine2 | undefined;
  if (side.apronCasing) {
    apronCasing = polyLine2SetCW(profileReproject(side.apronCasing, srcPlane, destPlane)[1]);
  }
  let stoolCasing: PolyLine2 | undefined;
  if (side.stoolCasing) {
    stoolCasing = polyLine2SetCW(profileReproject(side.stoolCasing, srcPlane, destPlane)[1]);
  }
  const sideCasing = side.sideCasing.map((poly) => {
    return poly.map((pline) => {
      return polyLine2SetCW(profileReproject(pline, srcPlane, destPlane)[1]);
    });
  });

  const layers =
    side.openingSideLayers &&
    Object.entries(side.openingSideLayers).reduce(
      (layers, [layerId, layer]) => {
        layers[layerId] = {
          ...layer,
          lines: layer.lines && polyLine2sWithPlaneReproject(layer.lines, srcPlane, destPlane),
          union: layer.union && polyLine2sWithPlaneReproject(layer.union, srcPlane, destPlane),
        };
        return layers;
      },
      {} as OpeningSide["openingSideLayers"],
    );

  return {
    ...side,
    openingRect2,
    opening2,
    opening3,
    openingCasing2,
    openingCasing3,
    apronCasing,
    stoolCasing,
    sideCasing,
    openingSideLayers: layers,
  };
}

function polyLine2sWithPlaneReproject(pp: PolyLine2sWithPlane, srcPlane: Plane, destPlane: Plane): PolyLine2sWithPlane {
  return {
    plane: destPlane,
    lines: pp.lines.map((p) => profileReproject(p, srcPlane, destPlane)[1]),
  };
}

export function profileReproject(profile: PolyLine2, srcPlane: Plane, destPlane: Plane): [PolyLine3, PolyLine2] {
  const opening3 = polyLine3OnPlane(profile, srcPlane);
  const onPlane = planeProjectPolyLine3(destPlane, opening3);
  return [polyLine3OnPlane(onPlane, destPlane), onPlane];
}

export function mirrorProfiles(profiles: OpeningSide): OpeningSide {
  const aabb2 = polyLine2AABB2(profiles.openingRect2);
  const centerX = (aabb2.maxX + aabb2.minX) / 2;

  const mirror2 = (p: PolyLine2) => {
    return mirrorPolyLine2XCopy(p, centerX);
  };
  // A opening2 should always be oriented and CCW
  const mirrorOpening = (p: PolyLine2) => profile2OrientCopy(polyLine2SetCCW(mirror2(p)));
  const mirror2Polygon = (p: PolyLine2Polygon) => p.map(mirror2);
  const mirrorLayer = (l: OpeningSideLayer) => {
    return {
      ...l,
      lines: l.lines && mirrorPlWithPlane(l.lines),
      union: l.union && mirrorPlWithPlane(l.union),
    };
  };
  const mirrorPlWithPlane = (pp: PolyLine2sWithPlane) => {
    return {
      plane: pp.plane,
      lines: pp.lines.map(mirror2),
    };
  };

  return {
    ...profiles,
    opening2: profiles.opening2 && mirrorOpening(profiles.opening2),
    opening2EdgeTypes: profiles.opening2EdgeTypes && [...profiles.opening2EdgeTypes].reverse(),
    openingRect2: profiles.openingRect2 && mirrorOpening(profiles.openingRect2),
    opening3: profiles.opening3 && mirrorPolyLine3(profiles.opening3),
    openingCasing2: profiles.openingCasing2 && mirrorOpening(profiles.openingCasing2),
    trim: profiles.trim.map((t) => {
      return {
        ...t,
        elevationProfile: t.elevationProfile && t.elevationProfile.map(mirror2),
      };
    }),
    headCasing: profiles.headCasing.map(mirror2),
    sideCasing: profiles.sideCasing.map(mirror2Polygon),
    apronCasing: profiles.apronCasing && mirror2(profiles.apronCasing),
    stoolCasing: profiles.stoolCasing && mirror2(profiles.stoolCasing),
    openingSideLayers: Object.entries(profiles.openingSideLayers).reduce(
      (layers, [layerId, layer]) => {
        layers[layerId] = mirrorLayer(layer);
        return layers;
      },
      {} as { [layerId: string]: OpeningSideLayer },
    ),
    openingOffsets2: Object.entries(profiles.openingOffsets2).reduce(
      (offsets, [offsetId, offset]) => {
        offsets[offsetId] = mirrorOpening(offset);
        return offsets;
      },
      {} as { [offsetId: string]: PolyLine2 },
    ),
    openingOffsets3: Object.entries(profiles.openingOffsets3).reduce(
      (offsets, [offsetId, offset]) => {
        offsets[offsetId] = mirrorPolyLine3(offset);
        return offsets;
      },
      {} as { [offsetId: string]: PolyLine3 },
    ),
  };
}
